import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { FlexLayoutProps } from '@swyftx/aviary/atoms/Layout/Flex/FlexLayout.styles';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { WARNING_THRESHOLD } from '@swyftx/margin/build/utils';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  className?: string;
  alignItems?: FlexLayoutProps['alignItems'];
  direction?: FlexLayoutProps['direction'];
  justifyContent?: FlexLayoutProps['justifyContent'];
  size?: NumericProps['size'];
  weight?: NumericProps['weight'];
};

const MarginAccountHealthMarginEquityWarning: React.FC<Props> = ({
  className,
  alignItems,
  direction,
  justifyContent,
  size = 'large',
  weight = 'bold',
}) => {
  const { t } = useTranslation('margin');
  const { account, currency, isLoading } = useMarginAccount();

  return (
    <FlexLayout
      spacing={8}
      direction={direction}
      alignItems={alignItems}
      justifyContent={justifyContent}
      className={className}
    >
      <EnhancedTooltip
        name={t('accountOverview.equityWarning.title')}
        title={t('accountOverview.equityWarning.tooltip')}
        align='start'
        side='top'
      >
        <Body color='secondary' size='xsmall' underline='dashed'>
          {t('accountOverview.equityWarning.title')}
        </Body>
      </EnhancedTooltip>
      <Numeric weight={weight} size={size} color='primary' loading={isLoading}>
        {formatCurrency(Big(account?.margin).times(Big(WARNING_THRESHOLD).div(100)), currency, {
          appendCode: true,
          hideCode: false,
        })}
      </Numeric>
    </FlexLayout>
  );
};

export { MarginAccountHealthMarginEquityWarning };
